import React, { FC, useEffect, useRef, useState } from "react";
import { useDataProvider, useNotify } from "react-admin";
import { GeneralContentItem } from "../../types/generalContent";
import { v4 as uuidv4 } from "uuid";
import {
  dotPathToBracketPath,
  updateNestedField,
  validateByTemplate,
} from "./helpers";
import DreamHomeCards from "./shared/DreamHomeCards";
import BonusDrawCards from "./shared/BonusDrawCards";
import TwoForOneCards from "./shared/TwoForOneCards";
import { FormTab, TabbedForm } from "react-admin";
import { CONTENT_TAB_THEME_CSS_VARS } from "./contentTabThemes";

import "./index.css";

const initialState: GeneralContentItem = {
  _id: "",
  dreamHome: {
    floorPlanUrl: "",
    floorPlanMobileUrl: "",
    video: {
      title: "Win this Dream Home",
      videoMobileUrl: "",
      videoDesktopUrl: "",
      videoPreviewMobileUrl: "",
      videoPreviewUrl: "",
      badgePrice: "£3m",
      badgeUrl: "",
    },

    info: [
      {
        _id: "",
        title: "",
        description: [
          {
            text: "",
            _id: "",
          },
        ],
      },
    ],

    content: {
      title: "The Dream Home",
      subtitle: "",
      icons: [
        {
          _id: "",
          iconUrl: "",
          iconTitle: "",
        },
      ],
      description: [""],
      imageUrl: "",
      imageX2Url: "",
      imageMobileUrl: "",
      imageMobileX2Url: "",
      imageText: [""],
    },

    accordionData: [
      {
        _id: "",
        iconUrl: "",
        title: "",
        description: "",
      },
    ],

    drawLine: {
      title: "Didn't Subscribe Last Month? Here's what you missed",
      description: "Your win. Your dream. Your choice.",
      stepper: [
        {
          _id: "",
          iconUrl: "",
          title: "",
          description: [""],
          isActive: false,
        },
      ],
      footerText:
        "Win your Dream Home to live in or rent out, or take the property value in tax-free cash.",
    },

    timeLine: {
      title: "",
      description: "",
      specialBadge: "Exclusive subscriber weekly £1,000 draws",
      stepper: [
        {
          _id: "",
          iconUrl: "",
          title: "",
          description: [""],
          isActive: false,
        },
      ],
      footerText: "Your win. Your dream. Your choice",
    },

    gallery: [
      {
        title: "",
        description: "",
        image800Url: "",
        image1600Url: "",
        image2048Url: "",
        image3840Url: "",
      },
    ],

    faq: {
      imageUrl: "",
    },

    bonusDraw: {
      videoUrl: "",
      videoPreviewUrl: "",
    },
  },

  bonusDraw: {
    title: "",
    badgePrice: "",
    badgeUrl: "",
    video: {
      videoMobileUrl: "",
      videoDesktopUrl: "",
      videoPreviewMobileUrl: "",
      videoPreviewUrl: "",
    },

    mainImage: {
      image800Url: "",
      image1600Url: "",
      image2048Url: "",
      image3840Url: "",
    },
    content: {
      title: "",
      imageUrl: "",
      imageX2Url: "",
      imageMobileUrl: "",
      imageMobileX2Url: "",
      icons: [
        {
          _id: "",
          iconUrl: "",
          iconTitle: "",
        },
      ],
      description: [""],
    },

    gallery: [
      {
        title: "",
        description: "",
        image800Url: "",
        image1600Url: "",
        image2048Url: "",
        image3840Url: "",
      },
    ],
  },

  twoForOne: {
    floorPlanUrl: "",
    floorPlanMobileUrl: "",
    stepperSubtitle: "",
    nextImageUrl: "",
    video: {
      title: "Win this Dream Home",
      videoMobileUrl: "",
      videoDesktopUrl: "",
      videoPreviewMobileUrl: "",
      videoPreviewUrl: "",
      badgePrice: "£3m",
      badgeUrl: "",
    },

    info: [
      {
        _id: "",
        title: "",
        description: [
          {
            text: "",
            _id: "",
          },
        ],
      },
    ],

    content: {
      title: "The 2-4-1 Offer",
      subtitle: "",
      imageUrl: "",
      imageX2Url: "",
      imageMobileUrl: "",
      imageMobileX2Url: "",
      imageText: [""],
      icons: [
        {
          _id: "",
          iconUrl: "",
          iconTitle: "",
        },
      ],
      description: [""],
    },

    accordionData: [
      {
        _id: "",
        iconUrl: "",
        title: "",
        description: "",
      },
    ],

    gallery: [
      {
        title: "",
        description: "",
        image800Url: "",
        image1600Url: "",
        image2048Url: "",
        image3840Url: "",
      },
    ],

    faq: {
      imageUrl: "",
      faqs: [],
    },
  },
};

export interface CollapseState {
  [key: string]: boolean;
}

const GeneralContent: FC = () => {
  const [state, setState] = useState<GeneralContentItem>(initialState);
  const stateRef = useRef<GeneralContentItem>(initialState);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [collapseState, setCollapseState] = useState<CollapseState>({
    dhVideo: true,
    dhInfo: false,
    dhContent: false,
    dhAccordionData: false,
    dhTimeLine: false,
    dhDrawLine: false,
    dhGallery: false,
    dhEarlyBird: false,
    dhFaq: false,
    bdMainImage: true,
    bdHeroVideo: false,
    bdContent: false,
    bdGallery: false,
    tfoVideo: true,
    tfoInfo: false,
    tfoContent: false,
    tfoAccordionData: false,
    tfoGallery: false,
    tfoFaq: false,
    tfoStepperSubtitle: false,
  });

  const notify = useNotify();
  const dataProvider = useDataProvider();

  const hydrateGeneralContentState = (
    payload: Partial<GeneralContentItem> = {},
  ): GeneralContentItem => {
    return {
      ...initialState,
      ...payload,
      dreamHome: {
        ...initialState.dreamHome,
        ...payload.dreamHome,
        video: {
          ...initialState.dreamHome.video,
          ...(payload.dreamHome?.video || {}),
        },
        content: {
          ...initialState.dreamHome.content,
          ...(payload.dreamHome?.content || {}),
        },
        faq: {
          ...initialState.dreamHome.faq,
          ...(payload.dreamHome?.faq || {}),
        },
        bonusDraw: {
          ...initialState.dreamHome.bonusDraw,
          ...(payload.dreamHome?.bonusDraw || {}),
        },
      },
      bonusDraw: {
        ...initialState.bonusDraw,
        ...payload.bonusDraw,
        video: {
          ...initialState.bonusDraw.video,
          ...(payload.bonusDraw?.video || {}),
        },
        mainImage: {
          ...initialState.bonusDraw.mainImage,
          ...(payload.bonusDraw?.mainImage || {}),
        },
        content: {
          ...initialState.bonusDraw.content,
          ...(payload.bonusDraw?.content || {}),
        },
      },
      twoForOne: {
        ...initialState.twoForOne,
        ...payload.twoForOne,
        video: {
          ...initialState.twoForOne.video,
          ...(payload.twoForOne?.video || {}),
        },
        info: payload.twoForOne?.info || initialState.twoForOne.info,
        content: {
          ...initialState.twoForOne.content,
          ...(payload.twoForOne?.content || {}),
        },
        faq: {
          ...initialState.twoForOne.faq,
          ...(payload.twoForOne?.faq || {}),
          faqs: (payload.twoForOne?.faq?.faqs ?? initialState.twoForOne.faq.faqs).map(
            (faq: GeneralContentItem["twoForOne"]["faq"]["faqs"][number]) => ({
              _id: faq._id || uuidv4(),
              question: faq.question || "",
              answer:
                Array.isArray(faq.answer) && faq.answer.length > 0
                  ? faq.answer
                  : [""],
            }),
          ),
        },
      },
    };
  };

  const getContent = async (): Promise<void> => {
    const result = (await dataProvider.getGeneralContent("/content")) as any;
    if (result.data && result.data.dreamHome) {
      const nextState = hydrateGeneralContentState(result.data);
      stateRef.current = nextState;
      setState(nextState);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const requiredFields = {
    dreamHome: {
      floorPlanUrl: true,
      floorPlanMobileUrl: true,
      video: {
        title: true,
        videoMobileUrl: true,
        videoDesktopUrl: true,
        videoPreviewMobileUrl: true,
        videoPreviewUrl: true,
      },
      info: [
        {
          title: true,
          description: [
            {
              text: true,
            },
          ],
        },
      ],
      content: {
        title: true,
        icons: [{ iconUrl: true, iconTitle: true }],
        description: [{ __self: true }],
        imageUrl: true,
        imageX2Url: true,
        imageMobileUrl: true,
        imageMobileX2Url: true,
        imageText: [{ __self: true }],
      },
      accordionData: [
        {
          iconUrl: true,
          title: true,
          description: true,
        },
      ],
      drawLine: {
        title: true,
        stepper: [
          {
            iconUrl: true,
            title: true,
            description: [{ __self: true }],
          },
        ],
      },
      timeLine: {
        title: true,
        description: true,
        stepper: [
          {
            iconUrl: true,
            title: true,
            description: [{ __self: true }],
            isActive: true,
          },
        ],
      },
      gallery: [
        {
          title: true,
          description: true,
          image800Url: true,
          image1600Url: true,
          image2048Url: true,
          image3840Url: true,
        },
      ],
      faq: {
        imageUrl: true,
      },
    },
    bonusDraw: {
      title: true,
      mainImage: {
        image800Url: true,
        image1600Url: true,
        image2048Url: true,
        image3840Url: true,
      },
      content: {
        imageUrl: true,
        imageX2Url: true,
        imageMobileUrl: true,
        imageMobileX2Url: true,
        icons: [
          {
            iconUrl: true,
            iconTitle: true,
          },
        ],
        description: [{ __self: true }],
      },
      gallery: [
        {
          title: true,
          description: true,
          image800Url: true,
          image1600Url: true,
          image2048Url: true,
          image3840Url: true,
        },
      ],
    },
    twoForOne: {
      floorPlanUrl: true,
      floorPlanMobileUrl: true,
      stepperSubtitle: true,
      nextImageUrl: true,
      video: {
        title: true,
        videoMobileUrl: true,
        videoDesktopUrl: true,
        videoPreviewMobileUrl: true,
        videoPreviewUrl: true,
      },
      info: [
        {
          title: true,
          description: [
            {
              text: true,
            },
          ],
        },
      ],
      content: {
        title: true,
        imageUrl: true,
        imageX2Url: true,
        imageMobileUrl: true,
        imageMobileX2Url: true,
        imageText: [{ __self: true }],
        icons: [{ iconUrl: true, iconTitle: true }],
        description: [{ __self: true }],
      },
      accordionData: [
        {
          iconUrl: true,
          title: true,
          description: true,
        },
      ],
      gallery: [
        {
          title: true,
          description: true,
          image800Url: true,
          image1600Url: true,
          image2048Url: true,
          image3840Url: true,
        },
      ],
      faq: {
        imageUrl: true,
      },
    },
  };

  const handleChange = (e: any) => {
    const { name, value } = e.currentTarget;
    console.log(name, value);
    
    let cleanedValue = value;
    try {
      cleanedValue = JSON.parse(`"${value}"`);
    } catch {}

    if (errors[name]) {
      const updatedErrors = errors;
      delete updatedErrors[name];
      setErrors(updatedErrors);
    }
    setState((prev) => {
      const next = updateNestedField(prev, name, cleanedValue);
      stateRef.current = next;
      return next;
    });
  };

  const handleAddInfoDescription = (name: string, type: string) => {
    let newItem = {};

    switch (type) {
      case "info":
        newItem = {
          _id: uuidv4(),
          subtitle: "",
          text: "",
          iconUrl: "",
        };
        break;
      case "icon":
        newItem = {
          _id: uuidv4(),
          iconTitle: "",
          iconDescription: "",
          iconUrl: "",
        };
        break;
      case "description":
        newItem = "";
        break;
      case "accordion":
        newItem = {
          _id: uuidv4(),
          iconUrl: "",
          title: "",
          description: "",
        };
        break;

      default:
        break;
    }

    setState((prev) => updateNestedField(prev, name, newItem));
  };

  const handleRemoveInfoDescriptionItem = (
    infoId: string,
    descriptionId: string,
  ) => {
    setState((prev) => ({
      ...prev,
      dreamHome: {
        ...prev.dreamHome,
        info: prev.dreamHome.info.map((infoItem) => {
          if (infoItem._id !== infoId) return infoItem;

          return {
            ...infoItem,
            description: infoItem.description.filter(
              (desc) => desc._id !== descriptionId,
            ),
          };
        }),
      },
    }));
  };

  const handleRemoveContentIconItem = (iconId: string, category: string) => {
    setState((prev) => ({
      ...prev,
      [category]: {
        ...prev[category],
        content: {
          ...prev[category].content,
          icons: prev[category].content.icons.filter(
            (icon) => icon._id !== iconId,
          ),
        },
      },
    }));
  };

  const handleRemoveContentDescriptionItem = (
    i: number,
    name: string,
    category: string,
  ) => {
    setState((prev) => ({
      ...prev,
      [category]: {
        ...prev[category],
        content: {
          ...prev[category].content,
          [name]: prev[category].content[name].filter(
            (_, index) => index !== i,
          ),
        },
      },
    }));
  };

  const handleChangeItemStatus = (name: string, value: boolean) => {
    setState((prev) => {
      const next = updateNestedField(prev, name, value);
      stateRef.current = next;
      return next;
    });
  };

  const handleRemoveAccordionItem = (id: string) => {
    setState((prev) => ({
      ...prev,
      dreamHome: {
        ...prev.dreamHome,
        accordionData: prev.dreamHome.accordionData.filter(
          (acc) => acc._id !== id,
        ),
      },
    }));
  };

  const moveAccordionItem = (index: number, direction: "up" | "down") => {
    setState((prev) => {
      const data = [...prev.dreamHome.accordionData];
      const newIndex = direction === "up" ? index - 1 : index + 1;

      if (newIndex < 0 || newIndex >= data.length) return prev;

      const temp = data[index];
      data[index] = data[newIndex];
      data[newIndex] = temp;

      return {
        ...prev,
        dreamHome: {
          ...prev.dreamHome,
          accordionData: data,
        },
      };
    });
  };

  const handleRemoveTimeLineDescriptionItem = (i: number, stepId: string) => {
    setState((prev) => ({
      ...prev,
      dreamHome: {
        ...prev.dreamHome,
        timeLine: {
          ...prev.dreamHome.timeLine,
          stepper: prev.dreamHome.timeLine.stepper.map((step) => {
            if (step._id !== stepId) return step;
            return {
              ...step,
              description: step.description.filter((_, index) => index !== i),
            };
          }),
        },
      },
    }));
  };

  const handleRemoveDrawLineDescriptionItem = (i: number, stepId: string) => {
    setState((prev) => ({
      ...prev,
      dreamHome: {
        ...prev.dreamHome,
        drawLine: {
          ...prev.dreamHome.drawLine,
          stepper: prev.dreamHome.drawLine.stepper.map((step) => {
            if (step._id !== stepId) return step;
            return {
              ...step,
              description: step.description.filter((_, index) => index !== i),
            };
          }),
        },
      },
    }));
  };

  const handleFormCancel = () => {
    setIsLoading(true);
    setErrors({});
    getContent();
  };

  const handleRemoveGalleryItem = (i: number, category: string) => {
    setState((prev) => ({
      ...prev,
      [category]: {
        ...prev[category],
        gallery: prev[category].gallery.filter((_, index) => index !== i),
      },
    }));
  };

  const handleAddFaqItem = () => {
    setState((prev) => ({
      ...prev,
      twoForOne: {
        ...prev.twoForOne,
        faq: {
          ...prev.twoForOne.faq,
          faqs: [
            ...prev.twoForOne.faq.faqs,
            { _id: uuidv4(), question: "", answer: [""] },
          ],
        },
      },
    }));
  };

  const handleRemoveFaqItem = (faqId: string) => {
    setState((prev) => ({
      ...prev,
      twoForOne: {
        ...prev.twoForOne,
        faq: {
          ...prev.twoForOne.faq,
          faqs: prev.twoForOne.faq.faqs.filter((faq) => faq._id !== faqId),
        },
      },
    }));
  };

  const handleAddFaqAnswer = (faqIndex: number) => {
    setState((prev) => {
      const faqs = prev.twoForOne.faq.faqs.map((faq, index) =>
        index === faqIndex
          ? { ...faq, answer: [...faq.answer, ""] }
          : faq,
      );
      return {
        ...prev,
        twoForOne: {
          ...prev.twoForOne,
          faq: { ...prev.twoForOne.faq, faqs },
        },
      };
    });
  };

  const handleRemoveFaqAnswer = (faqIndex: number, answerIndex: number) => {
    setState((prev) => {
      const faqs = prev.twoForOne.faq.faqs.map((faq, index) =>
        index === faqIndex
          ? {
              ...faq,
              answer: faq.answer.filter((_, i) => i !== answerIndex),
            }
          : faq,
      );
      return {
        ...prev,
        twoForOne: {
          ...prev.twoForOne,
          faq: { ...prev.twoForOne.faq, faqs },
        },
      };
    });
  };

  const validateTwoForOneFaqs = (
    faqs: GeneralContentItem["twoForOne"]["faq"]["faqs"],
  ): Record<string, string> => {
    const faqErrors: Record<string, string> = {};

    faqs.forEach((faq, faqIndex) => {
      if (!faq.question?.trim()) {
        faqErrors[`twoForOne.faq.faqs.${faqIndex}.question`] =
          "Field is required";
      }

      if (!Array.isArray(faq.answer) || faq.answer.length === 0) {
        faqErrors[`twoForOne.faq.faqs.${faqIndex}.answer.0`] =
          "At least one answer is required";
      }
    });

    return faqErrors;
  };

  const handleSave = async () => {
    setIsLoading(true);
    const currentState = stateRef.current;
    console.log("currentState", currentState);
    
    const errors = {
      ...validateByTemplate(currentState, requiredFields),
      ...validateTwoForOneFaqs(currentState.twoForOne.faq.faqs),
    };
    setErrors(errors);
    try {
      if (Object.keys(errors).length > 0) {
        openCollapseWithErrors(errors);
        const firstErrorKey = Object.keys(errors)[0];
        if (firstErrorKey) {
          const el = document.querySelector(
            `[name="${dotPathToBracketPath(firstErrorKey)}"]`,
          );
          if (el instanceof HTMLElement) {
            el.scrollIntoView({ behavior: "smooth", block: "center" });
            el.focus();
          }
        }
        setIsLoading(false);
        notify("Please check fields ", "error");
        return;
      } else {
        const result = (await dataProvider.editGeneralContent(
          "/content",
          currentState,
        )) as any;
        if (result.data) {
          notify("Content saved", "success");
        }
  
        setIsLoading(false);
      }
    } catch (err: any) {
      setIsLoading(false);
      notify(err.data.error || 'Error saving content', 'error');
    }
  };

  const toggleCard = (name: string) => {
    setCollapseState((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  const openCollapseWithErrors = (errors: Record<string, string>) => {
    const errorsSet = new Set();
    for (let i = 0; i <= Object.keys(errors).length - 1; i++) {
      const errorKey = Object.keys(errors)[i];
      const errorKeyParts = errorKey.replace(/\[(\d+)\]/g, ".$1").split(".");
      const sectionPrefix =
        errorKeyParts[0] === "dreamHome"
          ? "dh"
          : errorKeyParts[0] === "twoForOne"
            ? "tfo"
            : "bd";
      const errorSet =
        sectionPrefix +
        errorKeyParts[1].charAt(0).toUpperCase() +
        errorKeyParts[1].slice(1);
      if (
        errorSet === "dhFloorPlanUrl" ||
        errorSet === "dhFloorPlanMobileUrl"
      ) {
        errorsSet.add("bdVideo");
      } else if (errorSet === "bdTitle") {
        errorsSet.add("bdMainImage");
      } else {
        errorsSet.add(errorSet);
      }
    }

    if (errorsSet.size) {
      const errorObject = Object.fromEntries(
        Array.from(errorsSet, (key) => [key, true]),
      );
      console.log(errorObject);

      setCollapseState((prev) => ({ ...prev, ...errorObject }));
    }
  };

  return (
    <div className="content">
      <h1 className="content__title">General Page Content</h1>
      <div
        className="content-tabs-theme"
        style={CONTENT_TAB_THEME_CSS_VARS as React.CSSProperties}
      >
      <TabbedForm className="user-tabs content-tabs" toolbar={<></>}>
        <FormTab
          label="Home Page"
          className="content-tab content-tab--home"
          contentClassName="content-tab-panel content-tab-panel--home"
        >
          <DreamHomeCards
            state={state.dreamHome}
            collapseState={collapseState}
            toggleCard={toggleCard}
            errors={errors}
            handleChange={handleChange}
            handleAddInfoDescription={handleAddInfoDescription}
            handleRemoveInfoDescriptionItem={handleRemoveInfoDescriptionItem}
            handleRemoveContentIconItem={handleRemoveContentIconItem}
            handleRemoveContentDescriptionItem={
              handleRemoveContentDescriptionItem
            }
            handleChangeItemStatus={handleChangeItemStatus}
            moveAccordionItem={moveAccordionItem}
            handleRemoveAccordionItem={handleRemoveAccordionItem}
            handleRemoveTimeLineDescriptionItem={
              handleRemoveTimeLineDescriptionItem
            }
            handleRemoveGalleryItem={handleRemoveGalleryItem}
            handleRemoveDrawLineDescriptionItem={
              handleRemoveDrawLineDescriptionItem
            }
          />
        </FormTab>
        <FormTab
          label="Bonus Draw"
          className="content-tab content-tab--bonus"
          contentClassName="content-tab-panel content-tab-panel--bonus"
        >
          <BonusDrawCards
            state={state.bonusDraw}
            collapseState={collapseState}
            toggleCard={toggleCard}
            errors={errors}
            handleChange={handleChange}
            handleAddInfoDescription={handleAddInfoDescription}
            handleRemoveContentIconItem={handleRemoveContentIconItem}
            handleRemoveContentDescriptionItem={
              handleRemoveContentDescriptionItem
            }
            handleRemoveGalleryItem={handleRemoveGalleryItem}
          />
        </FormTab>
        <FormTab
          label="2-4-1 Page"
          className="content-tab content-tab--two-for-one"
          contentClassName="content-tab-panel content-tab-panel--two-for-one"
        >
          <TwoForOneCards
            state={state.twoForOne}
            collapseState={collapseState}
            toggleCard={toggleCard}
            errors={errors}
            handleChange={handleChange}
            handleAddInfoDescription={handleAddInfoDescription}
            handleRemoveInfoDescriptionItem={handleRemoveInfoDescriptionItem}
            handleRemoveContentIconItem={handleRemoveContentIconItem}
            handleRemoveContentDescriptionItem={
              handleRemoveContentDescriptionItem
            }
            handleChangeItemStatus={handleChangeItemStatus}
            moveAccordionItem={moveAccordionItem}
            handleRemoveAccordionItem={handleRemoveAccordionItem}
            handleRemoveGalleryItem={handleRemoveGalleryItem}
            handleAddFaqItem={handleAddFaqItem}
            handleRemoveFaqItem={handleRemoveFaqItem}
            handleAddFaqAnswer={handleAddFaqAnswer}
            handleRemoveFaqAnswer={handleRemoveFaqAnswer}
          />
        </FormTab>
      </TabbedForm>
      </div>
      {/* 

       */}

      <div className="content__btns">
        <button
          className="content__cancel-btn"
          type="button"
          disabled={isLoading}
          onClick={handleFormCancel}
        >
          Cancel
        </button>
        <button
          className="content__save-btn"
          type="button"
          disabled={isLoading}
          onClick={handleSave}
        >
          Save
        </button>
      </div>
    </div>
  );
};

export default GeneralContent;
